import { ISydDocument } from 'models/shared.models'
import {
  IAccountingStepContinueButtonProps,
  TAccountStepFiveValidationSteps,
  TAccountingStepConditionPair,
} from 'models/accounting.models'
import {
  ACCOUNTING_PROCESS_STEPS,
  ACCOUNTING_STEP_FIVE_VALIDATION_STEPS,
} from 'lib/enums/accounting.enums'
import {
  checkforInvalidUploadContinue,
  getStepFiveValidationConditionsAndSteps,
} from 'lib/utils/Accounting/accounting.utils'
import { useAccountingContext } from 'containers/Accounting/AccountingContainer/AccountingContainer'
import { IUseAccountingStepFive } from './useAccountingStepFive'

interface IUseAccountingStepFiveValidationStepsProps
  extends Pick<
    IUseAccountingStepFive,
    | 'additionalDocuments'
    | 'firstCategoryFileStatuses'
    | 'thirdCategoryFileStatuses'
    | 'firstCategoryFilesToValidate'
    | 'uploadedFirstCategoryFiles'
    | 'uploadedFourthCategoryFiles'
    | 'uploadedAndValidatedFirstCategoryFiles'
  > {
  totalThirdCategoryFiles: ISydDocument[]
  uploadedThirdCategoryFiles: ISydDocument[]
  thirdCategoryFilesToValidate: ISydDocument[]
  firstCategoryValidFiles: ISydDocument[]
  thirdCategoryValidFiles: ISydDocument[]
  firstCategoryInvalidFiles: ISydDocument[]
  thirdCategoryInvalidFiles: ISydDocument[]
  uploadedAndValidatedThirdCategoryFiles: ISydDocument[]
}

interface IUseAccountingStepFiveValidationSteps {
  continueButtonProps: IAccountingStepContinueButtonProps
}

const useAccountingStepFiveValidationSteps = ({
  totalThirdCategoryFiles,
  additionalDocuments,
  firstCategoryFileStatuses,
  thirdCategoryFileStatuses,
  uploadedFirstCategoryFiles,
  uploadedThirdCategoryFiles,
  uploadedFourthCategoryFiles,
  firstCategoryFilesToValidate,
  thirdCategoryFilesToValidate,
  firstCategoryValidFiles,
  thirdCategoryValidFiles,
  firstCategoryInvalidFiles,
  thirdCategoryInvalidFiles,
  uploadedAndValidatedFirstCategoryFiles,
  uploadedAndValidatedThirdCategoryFiles,
}: IUseAccountingStepFiveValidationStepsProps): IUseAccountingStepFiveValidationSteps => {
  const {
    isAllAccountingTasksComplete,
    handleStepSubmit,
    runValidationServiceOnSelectedFiles,
  } = useAccountingContext()

  const handleFirstCategoryFilesSubmit = (_e: any) => {
    runValidationServiceOnSelectedFiles(
      ACCOUNTING_PROCESS_STEPS.IMPUESTOS_PAGADOS,
      firstCategoryValidFiles,
      firstCategoryInvalidFiles
    )
  }

  const handleThirdCategoryFilesSubmit = (_e: any) => {
    runValidationServiceOnSelectedFiles(
      ACCOUNTING_PROCESS_STEPS.IMPUESTOS_PAGADOS,
      thirdCategoryValidFiles,
      thirdCategoryInvalidFiles,
      isAllAccountingTasksComplete,
      true
    )
  }

  const validationConditionsAndSteps: TAccountingStepConditionPair<TAccountStepFiveValidationSteps>[] =
    getStepFiveValidationConditionsAndSteps(
      totalThirdCategoryFiles,
      uploadedFirstCategoryFiles,
      uploadedThirdCategoryFiles,
      firstCategoryFilesToValidate,
      thirdCategoryFilesToValidate,
      firstCategoryFileStatuses,
      uploadedAndValidatedFirstCategoryFiles,
      uploadedAndValidatedThirdCategoryFiles
    )

  const getValidationStep = (): TAccountStepFiveValidationSteps => {
    for (const [condition, step] of validationConditionsAndSteps) {
      if (condition()) {
        return step
      }
    }
    return ACCOUNTING_STEP_FIVE_VALIDATION_STEPS.DEFAULT
  }

  const continueButtonPropsMap = {
    [ACCOUNTING_STEP_FIVE_VALIDATION_STEPS.FIRST_CATEGORY_FILES_UPLOADED]: {
      onClick: () =>
        handleStepSubmit(
          ACCOUNTING_PROCESS_STEPS.IMPUESTOS_PAGADOS,
          '',
          uploadedFirstCategoryFiles
        ),
      disabled: false,
      text: 'Continuar',
    },
    [ACCOUNTING_STEP_FIVE_VALIDATION_STEPS.FIRST_CATEGORY_FILES_VALIDATION]: {
      onClick: handleFirstCategoryFilesSubmit,
      disabled: checkforInvalidUploadContinue(firstCategoryFileStatuses),
      text: 'Continuar',
    },
    [ACCOUNTING_STEP_FIVE_VALIDATION_STEPS.THIRD_CATEGORY_FILES_UPLOADED]: {
      onClick: () =>
        handleStepSubmit(
          ACCOUNTING_PROCESS_STEPS.IMPUESTOS_PAGADOS,
          '',
          uploadedThirdCategoryFiles
        ),
      disabled: false,
      text: 'Continuar',
    },
    [ACCOUNTING_STEP_FIVE_VALIDATION_STEPS.THIRD_CATEGORY_FILES_VALIDATION]: {
      onClick: handleThirdCategoryFilesSubmit,
      disabled:
        checkforInvalidUploadContinue(thirdCategoryFileStatuses) ||
        !uploadedAndValidatedFirstCategoryFiles.length ||
        (additionalDocuments && !uploadedFourthCategoryFiles.length) ||
        !isAllAccountingTasksComplete,
      text:
        !checkforInvalidUploadContinue(thirdCategoryFileStatuses) &&
        !thirdCategoryFileStatuses.some(({ status }) => status === 'invalid')
          ? 'Finalizar servicio'
          : 'Continuar',
    },
    [ACCOUNTING_STEP_FIVE_VALIDATION_STEPS.DEFAULT]: {
      disabled: true,
      text: 'Continuar',
    },
  }

  const step = getValidationStep()

  const continueButtonProps: IAccountingStepContinueButtonProps =
    continueButtonPropsMap[step]

  return {
    continueButtonProps,
  }
}

export default useAccountingStepFiveValidationSteps
